import type {KeyboardEvent as ReactKeyboardEvent} from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {AutoComplete, Input, Spin} from "antd";
import {SearchOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import {QuickSearchServiceContext} from "../../Contexts";
import {formatVulSourceType, VulSourceType} from "../../domain/VulSourceType";
import {QuickSearchResult, QuickSearchResultType} from "../../service/QuickSearchService";
import * as styles from "./QuickSearch.module.css";
import type {InputRef} from "antd/es/input";

const SEARCH_DEBOUNCE_MS = 250;
const MIN_SEARCH_LENGTH = 2;
const POPUP_WIDTH = 420;
const RESULT_GROUPS = [
    {type: QuickSearchResultType.SYSTEM_ACTION, label: "Actions"},
    {type: QuickSearchResultType.ORGANIZATION, label: "Organizations"},
    {type: QuickSearchResultType.USER, label: "Users"},
    {type: QuickSearchResultType.SBOM_PROJECT, label: "Projects"},
    {type: QuickSearchResultType.SBOM_PROJECT_GROUP, label: "Project groups"},
    {type: QuickSearchResultType.SBOM_PROJECT_VERSION, label: "Project versions"},
    {type: QuickSearchResultType.VIEW, label: "Views"},
    {type: QuickSearchResultType.VUL_SOURCE, label: "Sources"},
    {type: QuickSearchResultType.INTEREST, label: "Interests"}
];

function QuickSearch() {
    const quickSearchService = useContext(QuickSearchServiceContext);
    const navigate = useNavigate();
    const debounceHandle = useRef<ReturnType<typeof setTimeout>>();
    const inputRef = useRef<InputRef>(null);
    const requestId = useRef(0);
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<QuickSearchResult[]>([]);
    const [loading, setLoading] = useState(false);

    const options = useMemo(() => buildOptions(results), [results]);

    useEffect(() => {
        function handleKeyboardShortcut(event: KeyboardEvent) {
            if (!event.ctrlKey || event.altKey || event.metaKey || event.shiftKey || event.key.toLowerCase() !== "k") {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            inputRef.current?.focus();
        }

        window.addEventListener("keydown", handleKeyboardShortcut);

        return () => window.removeEventListener("keydown", handleKeyboardShortcut);
    }, []);

    useEffect(() => () => {
        if (debounceHandle.current) {
            clearTimeout(debounceHandle.current);
        }

        requestId.current += 1;
    }, []);

    return <AutoComplete
        className={styles.quickSearch}
        value={search}
        options={options}
        onSearch={onSearch}
        onSelect={onSelect}
        notFoundContent={loading ? <QuickSearchLoading/> : null}
        popupMatchSelectWidth={POPUP_WIDTH}>
        <Input
            allowClear
            placeholder="Quick search (Ctrl+K)"
            ref={inputRef}
            suffix={<SearchOutlined/>}
            onChange={event => {
                if (!event.target.value) {
                    clearResults();
                }
            }}
            onKeyDown={onKeyDown}
        />
    </AutoComplete>;

    function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
        if (event.key !== "Escape") {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        setSearch("");
        clearResults();
        inputRef.current?.blur();
    }

    function onSearch(value: string) {
        setSearch(value);

        if (debounceHandle.current) {
            clearTimeout(debounceHandle.current);
        }

        if (value.trim().length < MIN_SEARCH_LENGTH) {
            clearResults();
            return;
        }

        debounceHandle.current = setTimeout(() => runSearch(value.trim()), SEARCH_DEBOUNCE_MS);
    }

    function runSearch(value: string) {
        const currentRequestId = requestId.current + 1;

        requestId.current = currentRequestId;
        setLoading(true);

        quickSearchService.search(value)
            .then(searchResults => {
                if (requestId.current === currentRequestId) {
                    setResults(searchResults);
                }
            })
            .catch(() => {
                if (requestId.current === currentRequestId) {
                    setResults([]);
                }
            })
            .finally(() => {
                if (requestId.current === currentRequestId) {
                    setLoading(false);
                }
            });
    }

    function onSelect(url: string) {
        setSearch("");
        clearResults();
        navigate(url);
    }

    function clearResults() {
        if (debounceHandle.current) {
            clearTimeout(debounceHandle.current);
            debounceHandle.current = undefined;
        }

        requestId.current += 1;
        setLoading(false);
        setResults([]);
    }
}

function buildOptions(results: QuickSearchResult[]) {
    return RESULT_GROUPS
        .map(group => ({
            label: group.label,
            options: results
                .filter(result => result.type === group.type)
                .map(result => ({
                    value: result.url,
                    label: <QuickSearchOption result={result}/>
                }))
        }))
        .filter(group => group.options.length > 0);
}

function QuickSearchOption({result}: { result: QuickSearchResult }) {
    return <div className={styles.option}>
        <div className={styles.optionTitle}>{result.title}</div>
        {result.subtitle && <div className={styles.optionContext}>{formatSubtitle(result)}</div>}
    </div>;
}

function QuickSearchLoading() {
    return <div className={styles.loading}>
        <Spin size="small"/>
        <span>Searching...</span>
    </div>;
}

function formatSubtitle(result: QuickSearchResult) {
    switch (result.type) {
        case QuickSearchResultType.ORGANIZATION:
            return `ID: ${result.subtitle}`;
        case QuickSearchResultType.SBOM_PROJECT_VERSION:
            return `Project: ${result.subtitle}`;
        case QuickSearchResultType.VUL_SOURCE: {
            const sourceType = result.subtitle as VulSourceType;

            return `Type: ${formatVulSourceType(sourceType) ?? result.subtitle}`;
        }
        case QuickSearchResultType.INTEREST:
        case QuickSearchResultType.SBOM_PROJECT_GROUP:
        case QuickSearchResultType.USER:
            return `Organization: ${result.subtitle}`;
        default:
            return result.subtitle;
    }
}

export default QuickSearch;
